import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const EMPTY_HOUSES = {
  "RAYVERN-CLAW": { merits: 0, strikes: 0, total: 0 },
  "HUFF-N-PUFFF": { merits: 0, strikes: 0, total: 0 },
  "SLITHERY-INN": { merits: 0, strikes: 0, total: 0 },
  "GIBBONS-DOOR": { merits: 0, strikes: 0, total: 0 },
};

const HOUSE_COLORS = {
  "RAYVERN-CLAW": "#55acee",
  "HUFF-N-PUFFF": "#aa8ed6",
  "SLITHERY-INN": "#fdcb58",
  "GIBBONS-DOOR": "#896356",
};

const HousePoints = () => {
  const [houseData, setHouseData] = useState(EMPTY_HOUSES);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHousePoints = useCallback(async (refresh = false) => {
    try {
      setError(null);

      const url = refresh
        ? "/api/house-points?refresh=true"
        : "/api/house-points";

      const response = await fetch(url, {
        credentials: "include",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const mergedData = { ...EMPTY_HOUSES, ...(data || {}) };

      setHouseData(mergedData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("House Points fetch error:", err);
      setError(err.message || "Failed to load house points");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!isMounted) return;
      await fetchHousePoints(false);
    };

    load();

    const interval = setInterval(() => {
      if (isMounted) {
        fetchHousePoints(false);
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchHousePoints]);

  const houseArray = useMemo(() => {
    return Object.entries(houseData)
      .map(([house, stats]) => ({
        name: house,
        merits: Number(stats?.merits || 0),
        strikes: Number(stats?.strikes || 0),
        total: Number(stats?.total || 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [houseData]);

  const data = useMemo(() => {
    const houseNames = houseArray.map((h) => h.name);
    const houseTotals = houseArray.map((h) => h.total);
    const houseBarColors = houseNames.map((house) => HOUSE_COLORS[house] || "#666");

    return {
      labels: houseNames,
      datasets: [
        {
          label: "House Points",
          data: houseTotals,
          backgroundColor: houseBarColors,
          borderColor: "#222",
          borderWidth: 1,
        },
      ],
    };
  }, [houseArray]);

  const options = useMemo(() => {
    return {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: "🏆 House Points",
          font: { size: 18 },
          color: "#ffffff",
        },
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const house = houseArray[context.dataIndex];
              if (!house) return ` ${context.raw}`;

              return ` Total: ${house.total} | Merits: ${house.merits} | Strikes: ${house.strikes}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            color: "#ffffff",
          },
          grid: {
            color: "rgba(255,255,255,0.08)",
          },
        },
        y: {
          ticks: {
            autoSkip: false,
            color: "#ffffff",
          },
          grid: {
            color: "rgba(255,255,255,0.04)",
          },
        },
      },
    };
  }, [houseArray]);

  return (
    <div style={{ width: "100%", margin: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "#ffffff", fontSize: "0.95rem" }}>
          {loading
            ? "Loading House Cup data..."
            : lastUpdated
            ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
            : "House Cup data ready"}
        </div>

        <button
          onClick={() => fetchHousePoints(true)}
          style={{
            background: "#111",
            color: "#fff",
            border: "1px solid #333",
            padding: "8px 14px",
            cursor: "pointer",
            borderRadius: "6px",
          }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <p style={{ color: "#ff6b6b", marginBottom: "12px" }}>
          Error loading House Points: {error}
        </p>
      )}

      <div style={{ width: "100%", height: "500px", margin: "auto" }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default HousePoints;